// Staff view of ONE participant's completion (roster expander in the session
// sheet): the server-evaluated checklist, sign-off buttons for the viewer's
// role, Validate / Reject when the practical exercise awaits validation, and
// — learning:manage only — "Force complete", behind a confirm that says the
// override is audited (POST /complete {force:true}; 403 for anyone else).
import { useT } from '../lib/i18n';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Check, Loader2, ShieldAlert, X } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/features/shared/components/ui/alert-dialog';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { getApiErrorMessage } from '@/lib/apiError';
import type { ValidateInput } from '@/services/learningService';
import { useAssignmentCompletion, useCompleteAssignment, useValidateSubmission } from '../api/hooks';
import { CompletionChecklist } from './CompletionChecklist';
import { SignoffButtons } from './SignoffButtons';

interface Props {
  assignmentId: string;
  learnerName: string;
  canManage: boolean;
  canManagerSign: boolean;
  canTrainerSign: boolean;
  /** learning:manage | learning:assign — may read the Phase 6 event catalog (labels for verifications). */
  canReadCatalog?: boolean;
  className?: string;
}

export const ParticipantCompletion: React.FC<Props> = ({ assignmentId, learnerName, canManage, canManagerSign, canTrainerSign, canReadCatalog, className }) => {
  const t = useT();
  const q = useAssignmentCompletion(assignmentId);
  const validate = useValidateSubmission();
  const complete = useCompleteAssignment();
  const [decide, setDecide] = useState<{ decision: ValidateInput['decision']; score: string; note: string } | null>(null);
  const [forceOpen, setForceOpen] = useState(false);
  const view = q.data;

  if (q.isLoading) return <div className={`text-xs text-muted-foreground ${className || ''}`}>{t('training:loading', 'Loading')}…</div>;
  if (q.isError || !view) return <p className={`text-xs text-muted-foreground ${className || ''}`}>{getApiErrorMessage(q.error, t('training:completion_unavailable', 'Completion details are not available to you.'))}</p>;
  const sub = view.submission;
  const done = view.status === 'completed' || view.status === 'verified';

  const sendDecision = () => {
    if (!sub || !decide) return;
    const body: ValidateInput = { decision: decide.decision };
    if (decide.score.trim() !== '') body.score = Number(decide.score);
    if (decide.note.trim()) body.note = decide.note.trim();
    validate.mutate({ submissionId: sub.id, body, assignmentId }, {
      onSuccess: () => { toast.success(decide.decision === 'validated' ? t('training:validated_ok', 'Validated') : t('training:rejected_ok', 'Rejected')); setDecide(null); },
    });
  };

  return (
    <div className={`space-y-2 ${className || ''}`} data-testid={`participant-completion-${assignmentId}`}>
      <CompletionChecklist view={view} compact canReadCatalog={canReadCatalog} />
      {view.practice && (
        <div className="text-xs text-muted-foreground" data-testid="participant-practice">
          <span className="font-medium text-foreground">{view.practice.title}</span> · {sub ? (sub.status === 'submitted' ? t('training:sub_submitted', 'Awaiting validation') : sub.status === 'validated' ? t('training:sub_validated', 'Validated') : sub.status === 'rejected' ? t('training:sub_rejected', 'Rejected') : t('training:sub_pending', 'Not submitted')) : t('training:sub_pending', 'Not submitted')}
          {sub?.evidence_url && <> · <a className="text-orange-600 hover:underline" href={sub.evidence_url} target="_blank" rel="noreferrer">{t('training:view_evidence', 'View evidence')}</a></>}
          {sub?.score != null && <> · {t('training:score_n', 'Score: {{n}}', { n: sub.score })}</>}
        </div>
      )}
      {sub && sub.status === 'submitted' && !decide && (
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" className="h-7" onClick={() => setDecide({ decision: 'validated', score: sub.score != null ? String(sub.score) : '', note: '' })} data-testid="pc-validate"><Check className="mr-1 h-3.5 w-3.5" />{t('training:validate', 'Validate')}</Button>
          <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => setDecide({ decision: 'rejected', score: '', note: '' })} data-testid="pc-reject"><X className="mr-1 h-3.5 w-3.5" />{t('training:reject', 'Reject')}</Button>
        </div>
      )}
      {decide && (
        <div className="rounded-md border border-orange-300 bg-orange-50 dark:bg-orange-500/10 p-2 flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label htmlFor={`pc-score-${assignmentId}`} className="text-xs">{t('training:score_optional', 'Score (optional)')}</Label>
            <Input id={`pc-score-${assignmentId}`} className="h-8 w-24" type="number" min={0} max={100} value={decide.score} onChange={(e) => setDecide({ ...decide, score: e.target.value })} />
          </div>
          <div className="space-y-1 flex-1 min-w-[8rem]">
            <Label htmlFor={`pc-note-${assignmentId}`} className="text-xs">{t('training:note_optional', 'Note (optional)')}</Label>
            <Input id={`pc-note-${assignmentId}`} className="h-8" value={decide.note} onChange={(e) => setDecide({ ...decide, note: e.target.value })} />
          </div>
          <Button size="sm" variant="ghost" className="h-8" onClick={() => setDecide(null)} disabled={validate.isPending}>{t('training:cancel', 'Cancel')}</Button>
          <Button size="sm" className="h-8" variant={decide.decision === 'rejected' ? 'destructive' : 'default'} disabled={validate.isPending} onClick={sendDecision} data-testid="pc-confirm">
            {validate.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : decide.decision === 'validated' ? t('training:validate', 'Validate') : t('training:reject', 'Reject')}
          </Button>
        </div>
      )}
      <SignoffButtons view={view} canManagerSign={canManagerSign} canTrainerSign={canTrainerSign} idPrefix={`so-${assignmentId}`} />
      {canManage && !done && view.status !== 'cancelled' && (
        <>
          <Button size="sm" variant="ghost" className="h-7 text-amber-700 dark:text-amber-300" disabled={complete.isPending} onClick={() => setForceOpen(true)} data-testid="force-complete">
            <ShieldAlert className="mr-1 h-3.5 w-3.5" aria-hidden />{t('training:force_complete', 'Force complete')}
          </Button>
          <AlertDialog open={forceOpen} onOpenChange={setForceOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('training:force_complete_title', 'Force complete for {{name}}?', { name: learnerName })}</AlertDialogTitle>
                <AlertDialogDescription>{t('training:force_complete_help', 'This marks the assignment complete even though its completion policy is not satisfied. The override is audited and recorded against your name on the assignment.')}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('training:cancel', 'Cancel')}</AlertDialogCancel>
                <AlertDialogAction data-testid="force-complete-confirm" onClick={() => complete.mutate({ id: assignmentId, body: { force: true } }, { onSuccess: () => toast.success(t('training:completed_ok', 'Marked complete')) })}>
                  {t('training:force_complete', 'Force complete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
};

export default ParticipantCompletion;
